'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import { StatusPanel } from '@/components/status/status-panel'
import { PageShell } from '@/components/ui/page-shell'
import { SidebarInsetContent } from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { useHydrated } from '@/hooks/use-hydrated'
import { hasAdminToken, usePermissions } from '@/hooks/use-permissions'
import { navRequirementMet, routeRequirement } from '@/lib/auth/nav-permissions'
import { routeGatesItself, routeHasShell } from '@/lib/auth/route-shell'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'

/**
 * The page-level half of "menus hide what the role cannot open" (owner,
 * 2026-10-05). The sidebar already hides those entries; a bookmark or a pasted
 * link still reached the page, which rendered and then filled with red "could
 * not be loaded" errors from the API's 403s (browser QA: /settings/roles
 * without read:Role). This replaces such a page with one calm panel.
 *
 * The requirement is the page's own entry in lib/auth/nav-permissions.ts, or
 * the nearest mapped ancestor (`routeRequirement`). Open URLs and URLs nothing
 * maps (the console, the player, sign-in) pass through, at once.
 *
 * A page WITH a requirement:
 *   - grants KNOWN (`status === 'ready'`): mounts when they meet it, else the
 *     refusal;
 *   - grants LOADING: the page waits, NOT mounted (owner, 2026-10-06). It used
 *     to render while they loaded, so its queries left, met 403s, and only then
 *     did the refusal replace it — 348 refused calls in one browser sweep (a
 *     custom Helpdesk role opening any phishing page). Between client
 *     navigations the grants are already in the root client, so this is a
 *     hard load's wait for one request, GET /auth/me/permissions; the server
 *     render (no token there) is the wait too;
 *   - grants FAILED (`status === 'error'`): the page renders. Fail open — the
 *     API remains the boundary. So it does when nothing will ever ask for them
 *     (no session token in this browser): the page's own 401 signs out.
 *
 * WHERE: inside the app shell (owner, 2026-10-06). The refusal and the wait
 * land in the shell's content slot (SidebarInset, through
 * `SidebarInsetContent`), so the sidebar and a header with its toggle stay and
 * the person can navigate away. A route whose layout draws no shell
 * (lib/auth/route-shell.ts) gets the full-screen versions.
 *
 * WHEN NOT: a page whose link can name another workspace asks all this itself, once it has settled which workspace it is about
 * (`routeGatesItself`). The verdict here is for the workspace on screen, and that is not yet the page's.
 *
 * Mounted once in the root layout, around every page.
 */
export function RouteGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const pageVerdict = useRouteVerdict(pathname)
  const verdict: RouteVerdict = pathname && routeGatesItself(pathname) ? 'open' : pageVerdict
  const inShell = !!pathname && routeHasShell(pathname)
  const replacement = React.useMemo(
    () =>
      verdict === 'denied'
        ? { content: <RouteDeniedPanel placement="shell" /> }
        : verdict === 'waiting'
          ? { content: <RouteGateWaiting placement="shell" /> }
          : null,
    [verdict],
  )
  if (verdict === 'denied' && !inShell) return <RouteDeniedPanel />
  if (verdict === 'waiting' && !inShell) return <RouteGateWaiting />
  // Always this one element around the layout, whatever the verdict: switching
  // between a wrapper and none would rebuild the layout, sidebar included, the
  // moment the grants arrive.
  return <SidebarInsetContent.Provider value={replacement}>{children}</SidebarInsetContent.Provider>
}

/**
 * `open` — render the page; `waiting` — it has a requirement and the grants are
 * on their way; `denied` — the KNOWN grants do not meet it.
 */
export type RouteVerdict = 'open' | 'waiting' | 'denied'

export function useRouteVerdict(pathname: string | null): RouteVerdict {
  const access = usePermissions()
  const hydrated = useHydrated()
  const requirement = pathname ? routeRequirement(pathname) : undefined
  if (!requirement) return 'open'
  if (access.status === 'error') return 'open'
  if (access.status === 'loading') {
    // Before hydration (the server, the hydrating render) nobody can know yet.
    // After it, no token means the grants are never asked for: fail open.
    return hydrated && !hasAdminToken() ? 'open' : 'waiting'
  }
  const grants = access.data?.permissions
  if (!grants) return 'open'
  return navRequirementMet(requirement, grants) ? 'open' : 'denied'
}

type Placement = 'page' | 'shell'

/**
 * The refusal is the product's 403 page (owner, 2026-10-06: illustrated status
 * pages): the 403 picture, the gate's own words, "Back to Home" and "Go back".
 *
 * `shell` (the gate's normal case): drawn in the shell's content slot under a
 * header with the sidebar toggle — the slot is already the page's <main>.
 * `page`: the whole window, where no shell is drawn.
 */
export function RouteDeniedPanel({ placement = 'page' }: { placement?: Placement }) {
  const { t } = useShellTranslation()
  const panel = (
    <StatusPanel
      kind="403"
      variant={placement === 'shell' ? 'section' : 'page'}
      title={t('shell.permissions.routeDeniedTitle')}
      body={t('shell.permissions.routeDeniedBody')}
      primary={{ label: t('shell.permissions.backHome'), href: '/dashboard', icon: 'home' }}
    />
  )
  if (placement === 'shell') {
    return (
      <div data-testid="route-denied" data-placement="shell" className="flex min-h-0 w-full flex-1 flex-col">
        <PageShell breadcrumbs={[]}>{panel}</PageShell>
      </div>
    )
  }
  return (
    <main data-testid="route-denied" className="flex min-h-svh w-full flex-col bg-background">
      {panel}
    </main>
  )
}

/**
 * The gate's wait for the grants: a quiet skeleton where the page will be.
 * Said to a screen reader once ("Checking your access…"); nothing for the eye
 * to read, because it is usually gone within one request.
 */
export function RouteGateWaiting({ placement = 'page' }: { placement?: Placement }) {
  const { t } = useShellTranslation()
  const body = (
    <div
      role="status"
      aria-live="polite"
      data-testid="route-gate-waiting"
      data-placement={placement}
      className="flex w-full flex-col gap-4"
    >
      <span className="sr-only">{t('shell.permissions.checkingAccess')}</span>
      <Skeleton aria-hidden="true" className="h-8 w-56 max-w-full" />
      <Skeleton aria-hidden="true" className="h-4 w-96 max-w-full" />
      <Skeleton aria-hidden="true" className="mt-2 h-64 w-full" />
    </div>
  )
  if (placement === 'shell') return <PageShell breadcrumbs={[]}>{body}</PageShell>
  return <div className="flex min-h-svh w-full flex-col bg-background p-6">{body}</div>
}
